/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

/**
 * Design Tokens - Single Source of Truth
 *
 * ARCHITECTURE: CSS Variables are the ONLY source of truth for colors.
 * This module provides runtime access to CSS custom properties defined in globals.css.
 *
 * All color values are read dynamically from CSS variables at runtime, enabling:
 * - Theme-aware components that respond to organization customizations
 * - Multi-tenant support with per-organization color schemes
 * - Runtime color changes without rebuilding/redeploying
 * - Single source of truth (CSS variables in globals.css)
 *
 * PERFORMANCE: CSS variable reads are cached on first access to avoid expensive
 * DOM reflows. Call `refreshDesignTokenCache()` when theme changes to update cache.
 *
 * @see src/app/globals.css - MASTER source of all color definitions
 * @see tailwind.config.js - Synced with CSS variables for Tailwind classes
 *
 * Usage:
 * - For Tailwind classes: Use `bg-primary`, `text-primary`, etc. (recommended)
 * - For CSS variables: Use `hsl(var(--primary))` in inline styles
 * - For TypeScript: Import and use `getCSSVariable('--primary-500')`
 * - Theme changes: Call `refreshDesignTokenCache()` to update cached values
 */

/**
 * Reads a CSS custom property value from the document root
 * Returns the raw HSL value (e.g., "222 47% 11%")
 *
 * NOTE: This function is called only during cache initialization to avoid
 * performance overhead. Direct calls should be rare.
 */
export function getCSSVariable(varName: string): string {
  if (typeof window === "undefined") {
    // SSR fallback - return neutral gray
    return "0 0% 50%";
  }

  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(varName)
    .trim();

  return value || "0 0% 50%"; // Fallback to neutral gray
}

/**
 * Converts HSL CSS variable to hex color for legacy compatibility
 */
export function hslToHex(hsl: string): string {
  const parts = hsl.split(/\s+/).map((v) => parseFloat(v.replace("%", "")));
  const h = parts[0] ?? 0;
  const s = parts[1] ?? 0;
  const l = parts[2] ?? 50;

  const hue = h / 360;
  const saturation = s / 100;
  const lightness = l / 100;

  const hueToRgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };

  let r: number, g: number, b: number;

  if (saturation === 0) {
    r = g = b = lightness;
  } else {
    const q =
      lightness < 0.5
        ? lightness * (1 + saturation)
        : lightness + saturation - lightness * saturation;
    const p = 2 * lightness - q;
    r = hueToRgb(p, q, hue + 1 / 3);
    g = hueToRgb(p, q, hue);
    b = hueToRgb(p, q, hue - 1 / 3);
  }

  const toHex = (x: number) => {
    const hex = Math.round(x * 255).toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Cache structure for storing computed color values
 */
interface ColorCache {
  primary: Record<number, string>;
  semantic: {
    success: Record<number, string>;
    warning: Record<number, string>;
    error: Record<number, string>;
    info: Record<number, string>;
  };
}

/**
 * Color cache to avoid repeated DOM reads
 */
let colorCache: ColorCache | null = null;
let isCacheInitialized = false;

/**
 * Initialize the color cache by reading all CSS variables once
 * This function is called lazily on first access to design tokens
 */
function initializeCache(): void {
  if (typeof window === "undefined") {
    return; // Skip cache initialization on SSR
  }

  if (isCacheInitialized && colorCache) {
    return; // Already initialized
  }

  colorCache = {
    primary: {},
    semantic: {
      success: {},
      warning: {},
      error: {},
      info: {},
    },
  };

  // Cache all primary color scales
  const scaleKeys = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900];
  scaleKeys.forEach((key) => {
    const varName = key === 500 ? "--primary" : `--primary-${key}`;
    colorCache!.primary[key] = hslToHex(getCSSVariable(varName));
  });

  // Cache all semantic color scales
  const semanticScales = ["success", "warning", "error", "info"] as const;
  semanticScales.forEach((scale) => {
    scaleKeys.forEach((key) => {
      const varName = key === 500 ? `--${scale}` : `--${scale}-${key}`;
      colorCache!.semantic[scale][key] = hslToHex(getCSSVariable(varName));
    });
  });

  isCacheInitialized = true;
}

/**
 * Refresh the design token cache
 * Call this function when the theme changes to update cached color values
 *
 * @example
 * // After applying a new organization theme
 * applyOrganizationTheme(theme);
 * refreshDesignTokenCache();
 */
export function refreshDesignTokenCache(): void {
  isCacheInitialized = false;
  colorCache = null;
  initializeCache();
}

export type SemanticTokenScale = Record<number | string, string>;

export interface DesignTokens {
  colors: {
    primary: SemanticTokenScale;
    neutral: SemanticTokenScale;
    semantic: {
      success: SemanticTokenScale;
      warning: SemanticTokenScale;
      error: SemanticTokenScale;
      info: SemanticTokenScale;
    };
  };
  spacing: Record<string, string>;
  typography: {
    fontFamily: {
      sans: string[];
      mono: string[];
    };
    fontSize: Record<string, [string, { lineHeight: string }]>;
    fontWeight: Record<string, string>;
  };
  breakpoints: Record<string, string>;
  sizing: {
    button: Record<string, { height: string; padding: string }>;
    input: Record<string, { height: string }>;
  };
}

/**
 * Design Tokens - Dynamic Runtime Values
 *
 * All color values are read from CSS custom properties at runtime.
 * This enables theme-aware components and multi-tenant color customization.
 *
 * @see src/app/globals.css for the MASTER color definitions
 */
const SCALE_KEYS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900] as const;

function buildCachedScale(
  name: string,
  getCached: (shade: number) => string | undefined,
): SemanticTokenScale {
  const obj: SemanticTokenScale = {};
  for (const shade of SCALE_KEYS) {
    const varName = shade === 500 ? `--${name}` : `--${name}-${shade}`;
    Object.defineProperty(obj, shade, {
      get() {
        if (!isCacheInitialized) initializeCache();
        return getCached(shade) ?? hslToHex(getCSSVariable(varName));
      },
      enumerable: true,
    });
  }
  return obj;
}

const NEUTRAL_VALUES: Record<number, string> = {
  0: "#ffffff",
  50: "#f9fafb",
  100: "#f3f4f6",
  200: "#e5e7eb",
  300: "#d1d5db",
  400: "#9ca3af",
  500: "#6b7280",
  600: "#4b5563",
  700: "#374151",
  800: "#1f2937",
  900: "#111827",
};

export const designTokens: DesignTokens = {
  colors: {
    primary: buildCachedScale("primary", (s) => colorCache?.primary[s]),
    neutral: NEUTRAL_VALUES,
    semantic: {
      success: buildCachedScale(
        "success",
        (s) => colorCache?.semantic.success[s],
      ),
      warning: buildCachedScale(
        "warning",
        (s) => colorCache?.semantic.warning[s],
      ),
      error: buildCachedScale("error", (s) => colorCache?.semantic.error[s]),
      info: buildCachedScale("info", (s) => colorCache?.semantic.info[s]),
    },
  },
  spacing: {
    px: "1px",
    0: "0",
    1: "0.25rem",
    2: "0.5rem",
    3: "0.75rem",
    4: "1rem",
    5: "1.25rem",
    6: "1.5rem",
    8: "2rem",
    10: "2.5rem",
    12: "3rem",
    16: "4rem",
    20: "5rem",
    24: "6rem",
  },
  typography: {
    fontFamily: {
      sans: ["Inter", "system-ui", "sans-serif"],
      mono: ["JetBrains Mono", "Consolas", "monospace"],
    },
    fontSize: {
      xs: ["0.75rem", { lineHeight: "1rem" }],
      sm: ["0.875rem", { lineHeight: "1.25rem" }],
      base: ["1rem", { lineHeight: "1.5rem" }],
      lg: ["1.125rem", { lineHeight: "1.75rem" }],
      xl: ["1.25rem", { lineHeight: "1.75rem" }],
      "2xl": ["1.5rem", { lineHeight: "2rem" }],
      "3xl": ["1.875rem", { lineHeight: "2.25rem" }],
      "4xl": ["2.25rem", { lineHeight: "2.5rem" }],
    },
    fontWeight: {
      normal: "400",
      medium: "500",
      semibold: "600",
      bold: "700",
    },
  },
  breakpoints: {
    sm: "640px",
    md: "768px",
    lg: "1024px",
    xl: "1280px",
    "2xl": "1536px",
  },
  sizing: {
    button: {
      sm: { height: "2rem", padding: "0 0.75rem" },
      md: { height: "2.5rem", padding: "0 1rem" },
      lg: { height: "3rem", padding: "0 1.5rem" },
    },
    input: {
      sm: { height: "2rem" },
      md: { height: "2.5rem" },
      lg: { height: "3rem" },
    },
  },
};

/**
 * Convenience helper for reading semantic colors safely.
 */
export function getSemanticColorScale(
  scale: keyof DesignTokens["colors"]["semantic"],
): SemanticTokenScale {
  return designTokens.colors.semantic[scale];
}
